import { Collapse, List, Button, Tooltip, Switch } from "antd";
import { EditOutlined, DeleteOutlined, CheckOutlined, CloseOutlined, EyeOutlined } from "@ant-design/icons";
import { ReactComponent as BoxIcon } from "../../assets/images/BoxIcon.svg";
import { StyledButton } from "../../components/styles";
import { ItemListNameWrapper, ItemNameText, ItemNameWrapper, SelectedItem, StyledBody, StyledColumn, StyledHeader } from "../Category/styles";
import { useState } from "react";
import ConfirmationModal from "../../components/ConfirmationModal";
import { useAppDispatch, useAppSelector } from "../../utils/hooks/redux";
import { updateDeleteReason } from "../../redux/features/deleteReason/deleteReasonAsyncThunk";
import { toast } from "react-toastify";
import { DeleteReason, ReasonStatus, ReasonType } from "../../types/ReasonTypes";
import { ActivationStatus } from "../../types";

export const CollapseSection = ({ itemKey, label, activeKey, children }: any) => {
  return (
    <Collapse
      size="large"
      activeKey={activeKey}
      expandIcon={undefined}
      items={[
        {
          key: itemKey,
          label,
          children: <StyledBody>{children}</StyledBody>,
        },
      ]}
    />
  );
};

const ItemName: React.FC<{ className?: string; icon?: any; name: string }> = ({ className, icon, name }) => {
  return (
    <>
      <ItemNameWrapper className={className}>
        {icon && icon} <ItemNameText>{name}</ItemNameText>
      </ItemNameWrapper>
    </>
  );
};

interface ItemListProps {
  dataSource: any;
  loading: boolean;
  header?: any;
  onEdit: any;
  onDelete: any;
  onItemClick?: any;
  loadMore?: any;
  isSelected?: any;
  isSelectedItemOnClick?: any;
  hasAddPermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hasViewPermission?: boolean;
}

export const ItemList: React.FC<ItemListProps> = ({ header, dataSource, loading, onEdit, onDelete, onItemClick, loadMore, isSelected, isSelectedItemOnClick, hasAddPermission, hasEditPermission, hasDeletePermission, hasViewPermission }) => {
  const { loading: deleteReasonLoading } = useAppSelector((state) => state.deleteReason);
  const dispatch = useAppDispatch();
  const [showDeactivationConfirm, setShowDeactivationConfirm] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<DeleteReason | null>(null);

  const handleStatusChangeConfirm = async () => {
    try {
      if (selectedItem?.id) {
        await dispatch(updateDeleteReason({ id: selectedItem.id, type: ReasonType.Lost, reason: (selectedItem.reason as string), status: selectedItem?.status === ReasonStatus.DiActive ? ReasonStatus.Active : ReasonStatus.DiActive })).unwrap();
      }
      setShowDeactivationConfirm(false);
    } catch (error: any) {
      console.error("Status change failed:", error);
      toast.error(error || "Failed to change user status.");
    }
  };

  return (
    <>
      <List
        loading={loading}
        dataSource={dataSource}
        {...(loadMore && { loadMore: loadMore })}
        {...(header && { header: header })}
        renderItem={(item: any) => (
          <SelectedItem isSelected={isSelected ? item.id === isSelected : false} onClick={() => isSelectedItemOnClick && isSelectedItemOnClick(item)}>
            <List.Item
              style={{ flexWrap: "nowrap" }}
              actions={[
                (hasEditPermission && <Switch checkedChildren={<CheckOutlined />} unCheckedChildren={<CloseOutlined />} checked={item.checked} onChange={() => {
                  setShowDeactivationConfirm(true);
                  setSelectedItem(item);
                }} disabled={false} value={item.status === ActivationStatus.Active} />),
                (hasEditPermission && <Button size="small" icon={<EditOutlined />} onClick={() => onEdit(item)} type="link" />),
                (hasDeletePermission && <Button size="small" icon={<DeleteOutlined />} onClick={() => onDelete(item)} type="link" danger />),
                (hasViewPermission && !hasAddPermission && !hasEditPermission && <Button size="small" icon={<EyeOutlined />} onClick={() => onEdit(item)} type="link" />),
              ].filter(Boolean)}
            >
              <Tooltip placement="topLeft" title={item.name}>
                <ItemListNameWrapper onClick={() => onItemClick && onItemClick()}>
                  <ItemName icon={item.name && <BoxIcon />} name={item.name} />
                </ItemListNameWrapper>
              </Tooltip>
            </List.Item>
          </SelectedItem>
        )}
      />
      {showDeactivationConfirm && (
        <ConfirmationModal
          handleSave={handleStatusChangeConfirm}
          isModalOpen={showDeactivationConfirm}
          onCancel={() => setShowDeactivationConfirm(false)}
          title={`Confirm ${selectedItem?.status === ActivationStatus.Active ? "Deactivation" : "Activation"}`}
          message={`Are you sure you want to ${selectedItem?.status === ActivationStatus.Active ? "deactivate" : "activate"}?`}
          confirmButtonText={selectedItem?.status === ActivationStatus.Active ? "Deactivate" : "Activate"}
          loading={deleteReasonLoading}
        />
      )}
    </>
  );
};

interface ItemLabelProps {
  title: string;
  buttonLabel: string;
  onButtonClick?: any;
  onTitleClick?: any;
  hasAddPermission?: boolean;
}

export const ItemLabel: React.FC<ItemLabelProps> = ({ title, buttonLabel, onButtonClick, onTitleClick, hasAddPermission }) => (
  <StyledHeader onClick={() => onTitleClick && onTitleClick()}>
    <span>{title}</span>
    {hasAddPermission && <StyledButton
      type="primary"
      onClick={(e) => {
        onButtonClick && onButtonClick(e);
      }}
    >
      {buttonLabel}
    </StyledButton>}
  </StyledHeader>
);

interface ItemColumnProps {
  title: string;
  buttonLabel: string;
  onAddClick: () => void;
  dataSource: any;
  loading: boolean;
  header?: any;
  onEdit: any;
  onDelete: any;
  onItemClick?: any;
  loadMore?: any;
  isSelected?: any;
  isSelectedItemOnClick?: any;
  hasAddPermission?: boolean;
  hasEditPermission?: boolean;
  hasDeletePermission?: boolean;
  hasViewPermission?: boolean;
}

export const ItemColumn: React.FC<ItemColumnProps> = ({ title, buttonLabel, onAddClick, dataSource, loading, isSelected, isSelectedItemOnClick, onEdit, onDelete, header, loadMore, onItemClick, hasAddPermission, hasEditPermission, hasDeletePermission, hasViewPermission }) => (
  <StyledColumn>
    <ItemLabel title={title} buttonLabel={buttonLabel} onButtonClick={onAddClick} hasAddPermission={hasAddPermission} />
    <StyledBody>
      <ItemList
        header={header}
        loading={loading}
        dataSource={dataSource}
        isSelected={isSelected}
        isSelectedItemOnClick={isSelectedItemOnClick}
        onEdit={onEdit}
        onDelete={onDelete}
        loadMore={loadMore}
        onItemClick={onItemClick}
        hasAddPermission={hasAddPermission}
        hasEditPermission={hasEditPermission}
        hasDeletePermission={hasDeletePermission}
        hasViewPermission={hasViewPermission}
      />
    </StyledBody>
  </StyledColumn>
);
